import { useCallback, useEffect, useState } from 'react';
import {
    findWorkSpaceIdUsingCid,
    getCompanyList
} from '../api';
import { CompanyDataTransformer } from '../model/CompanyDataTransformer';
import {
    CompanyDetailResponseDTO,
    CompanySimpleResponseDTO,
    ModalState
} from '../types';

interface UseCompanyDataReturn {
  companies: CompanySimpleResponseDTO[];
  companyDetails: Record<string, CompanyDetailResponseDTO>;
  modalState: ModalState;
  refetch: () => void;
}

export const useCompanyData = (isOpen: boolean): UseCompanyDataReturn => {
  const [companies, setCompanies] = useState<CompanySimpleResponseDTO[]>([]);
  const [companyDetails, setCompanyDetails] = useState<Record<string, CompanyDetailResponseDTO>>({});
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    isLoading: false,
    error: null
  });

  const loadCompanyData = useCallback(async () => {
    if (!isOpen) return;

    setModalState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const cId = new URLSearchParams(window.location.search).get('cId');
      const workspaceResponse = await findWorkSpaceIdUsingCid(cId || "");
      const companiesResponse = await getCompanyList(workspaceResponse.data.workspaceName);
      
      // Filter out deleted companies and sort - primary company first
      const activeCompanies = companiesResponse.data.filter(company => !company.isDeleted);
      const sortedCompanies = CompanyDataTransformer.sortCompanies(activeCompanies);
      setCompanies(sortedCompanies);

      // workspace API에서 이미 모든 필요한 데이터를 포함하므로 개별 호출 불필요
      // 필요시 기본값으로 빈 객체 제공
      const detailsMap: Record<string, CompanyDetailResponseDTO> = {};
      sortedCompanies.forEach(company => {
        detailsMap[company.companyId] = {
          representative: {
            korName: company.bizCeoKorNm
          }
        };
      });

      setCompanyDetails(detailsMap);
      setModalState(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      setModalState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : '데이터를 불러오는데 실패했습니다.' 
      }));
    }
  }, [isOpen]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  return { 
    companies, 
    companyDetails, 
    modalState, 
    refetch: loadCompanyData 
  };
}; 